/**
 * Project memory (Blueprint v2 P6 / §9): `futurex-memory.md` at the repo root with
 * fixed headings, bootstrapped from the repo card + symbol index, and bounded by a
 * token-aware /compact that regenerates the DERIVED sections and archives an
 * overlong Decisions log to `.futurex/logs/`.
 */
import { existsSync, mkdirSync, readdirSync, writeFileSync } from 'node:fs';
import { join } from 'node:path';
import type { RepoCard } from '@fim/futurex-protocol';
import type { SymbolIndex } from '@fim/futurex-context';
import { MEMORY_FILENAME, readMemory, saveMemory } from './repo.js';

/** Fixed headings, in order. */
export const MEMORY_HEADINGS = [
  'Overview',
  'Stack',
  'Architecture map',
  'Key modules',
  'Conventions',
  'Decisions log',
  'Open tasks',
] as const;

const MEMORY_TITLE = '# FutureX project memory';

/** Sections regenerated from the repo (card + symbol index) on bootstrap/compact. */
const DERIVED = new Set<string>(['Overview', 'Stack', 'Architecture map', 'Key modules']);

const PLACEHOLDERS: Record<string, string> = {
  Conventions: '_(Add project conventions — naming, structure, testing — here.)_',
  'Decisions log': '_(Append decisions as `- YYYY-MM-DD: <decision>`.)_',
  'Open tasks': '_(Track open tasks here.)_',
};

/** ~chars-per-token heuristic (no tokenizer dependency). */
export function estimateTokens(text: string): number {
  return Math.ceil(text.length / 4);
}

/** Soft ceiling: above this, /compact regenerates derived sections + archives decisions. */
export const MEMORY_TOKEN_LIMIT = 7000;

function overviewSection(card: RepoCard | null): string {
  const name = card?.name ?? 'This project';
  const stack = [card?.framework, card?.backend, card?.orm]
    .filter((x): x is string => Boolean(x))
    .join(' + ');
  return `${name}${stack ? ` — ${stack}` : ''}. _(Edit to describe the project's purpose & goals.)_`;
}

function stackSection(card: RepoCard | null): string {
  if (!card) return '_(no manifest detected)_';
  const rows: Array<[string, string | undefined]> = [
    ['Language', card.language],
    ['Framework', card.framework],
    ['Backend', card.backend],
    ['ORM', card.orm],
    ['Package manager', card.packageManager],
    ['Tests', card.test],
  ];
  const lines = rows.filter((r): r is [string, string] => Boolean(r[1])).map(([k, v]) => `- ${k}: ${v}`);
  return lines.length > 0 ? lines.join('\n') : '_(stack not detected)_';
}

function architectureSection(root: string): string {
  let dirs: string[];
  try {
    dirs = readdirSync(root, { withFileTypes: true })
      .filter((d) => d.isDirectory() && !d.name.startsWith('.') && d.name !== 'node_modules')
      .map((d) => d.name)
      .sort()
      .slice(0, 30);
  } catch {
    return '_(unreadable)_';
  }
  return dirs.length > 0 ? dirs.map((d) => `- \`${d}/\``).join('\n') : '_(no top-level directories)_';
}

function keyModulesSection(symbols: SymbolIndex | null): string {
  if (!symbols) return '_(run `futurex index` to populate from the symbol index)_';
  const byFile = new Map<string, number>();
  for (const d of symbols.all()) {
    if (d.exported) byFile.set(d.file, (byFile.get(d.file) ?? 0) + 1);
  }
  const top = [...byFile.entries()].sort((a, b) => b[1] - a[1]).slice(0, 15);
  return top.length > 0
    ? top.map(([f, n]) => `- \`${f}\` — ${n} exported symbol${n === 1 ? '' : 's'}`).join('\n')
    : '_(no exported symbols indexed)_';
}

function derivedSection(
  heading: string,
  root: string,
  card: RepoCard | null,
  symbols: SymbolIndex | null,
): string {
  switch (heading) {
    case 'Overview':
      return overviewSection(card);
    case 'Stack':
      return stackSection(card);
    case 'Architecture map':
      return architectureSection(root);
    case 'Key modules':
      return keyModulesSection(symbols);
    default:
      return '';
  }
}

/** Parse a memory markdown into a heading→body map (bodies trimmed). */
export function parseMemorySections(md: string): Map<string, string> {
  const out = new Map<string, string>();
  for (const part of md.split(/^## /m)) {
    const nl = part.indexOf('\n');
    if (nl === -1) continue;
    const heading = part.slice(0, nl).trim();
    if (!heading || heading.startsWith('#')) continue; // skip the title block
    out.set(heading, part.slice(nl + 1).trim());
  }
  return out;
}

/** Render sections in the fixed heading order, appending any extras. */
function renderMemory(sections: Map<string, string>): string {
  const blocks: string[] = [MEMORY_TITLE];
  const seen = new Set<string>();
  for (const heading of MEMORY_HEADINGS) {
    seen.add(heading);
    blocks.push(`## ${heading}\n\n${sections.get(heading) ?? PLACEHOLDERS[heading] ?? '_(empty)_'}`);
  }
  for (const [heading, body] of sections) {
    if (!seen.has(heading)) blocks.push(`## ${heading}\n\n${body}`);
  }
  return `${blocks.join('\n\n')}\n`;
}

/** Build a provisional memory skeleton from the repo card + symbol index. */
export function buildMemorySkeleton(
  root: string,
  card: RepoCard | null,
  symbols: SymbolIndex | null,
): string {
  const sections = new Map<string, string>();
  for (const heading of MEMORY_HEADINGS) {
    sections.set(
      heading,
      DERIVED.has(heading) ? derivedSection(heading, root, card, symbols) : PLACEHOLDERS[heading] ?? '',
    );
  }
  return renderMemory(sections);
}

/** Write a provisional futurex-memory.md if absent. Returns whether it was created. */
export function bootstrapMemory(
  root: string,
  card: RepoCard | null,
  symbols: SymbolIndex | null,
): boolean {
  if (readMemory(root) !== null || existsSync(join(root, MEMORY_FILENAME))) return false;
  saveMemory(root, buildMemorySkeleton(root, card, symbols));
  return true;
}

export interface CompactResult {
  action: 'compacted' | 'unchanged' | 'absent';
  beforeTokens: number;
  afterTokens: number;
  archived?: string;
}

function archiveDecisions(root: string, lines: string[]): string {
  const stamp = new Date().toISOString().replace(/[:.]/g, '-');
  const relPosix = `.futurex/logs/decisions-${stamp}.md`;
  mkdirSync(join(root, '.futurex', 'logs'), { recursive: true });
  writeFileSync(join(root, '.futurex', 'logs', `decisions-${stamp}.md`), `# Archived decisions (${stamp})\n\n${lines.join('\n')}\n`, 'utf8');
  return relPosix;
}

/**
 * Bound memory growth: when over {@link MEMORY_TOKEN_LIMIT}, regenerate the derived
 * sections (Overview/Stack/Architecture/Key modules from the live card + symbol
 * index) and compress the Decisions log — keep the latest 20 entries inline, archive
 * the rest to `.futurex/logs/`. Below the limit it is a no-op.
 */
export function compactMemory(
  root: string,
  card: RepoCard | null,
  symbols: SymbolIndex | null,
): CompactResult {
  const existing = readMemory(root);
  if (existing === null) return { action: 'absent', beforeTokens: 0, afterTokens: 0 };
  const before = estimateTokens(existing);
  if (before <= MEMORY_TOKEN_LIMIT) return { action: 'unchanged', beforeTokens: before, afterTokens: before };

  const sections = parseMemorySections(existing);
  for (const heading of DERIVED) sections.set(heading, derivedSection(heading, root, card, symbols));

  let archived: string | undefined;
  const entries = (sections.get('Decisions log') ?? '').split('\n').filter((l) => l.trim().startsWith('- '));
  if (entries.length > 20) {
    archived = archiveDecisions(root, entries.slice(0, -20));
    sections.set('Decisions log', [`_(older entries archived to \`${archived}\`)_`, '', ...entries.slice(-20)].join('\n'));
  }

  const rebuilt = renderMemory(sections);
  saveMemory(root, rebuilt);
  return { action: 'compacted', beforeTokens: before, afterTokens: estimateTokens(rebuilt), archived };
}
